import { Directionality } from '@angular/cdk/bidi';
import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType, TemplatePortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Optional,
  SkipSelf,
  StaticProvider,
  TemplateRef,
} from '@angular/core';
import { of as observableOf } from 'rxjs';

import { ConfirmationDialogComponent } from '@shared/components/dialogs/confirmation-dialog';
import { ConfirmationDialogViewModel } from '@shared/components/dialogs/confirmation-dialog/confirmation-dialog.view-model';

import { DIALOG_DATA, DialogConfig } from './dialog-config';
import { DialogContainerComponent } from './dialog-container';
import { DialogRef } from './dialog-ref';

/** Injection token that can be used to specify default dialog options. */
export const DIALOG_DEFAULT_OPTIONS = new InjectionToken<DialogConfig>('dialog-default-options');

/**
 * Applies default options to the dialog config.
 *
 * @param defaults Object containing the default values to which to fall back.
 * @param config The configuration to which the defaults will be applied.
 * @returns The new configuration object with defaults applied.
 */
const _applyConfigDefaults = (defaults: DialogConfig, config?: DialogConfig): DialogConfig => {
  return { ...defaults, ...config };
};

@Injectable()
export class DialogService implements OnDestroy {
  private _dialogRefsAtThisLevel: DialogRef<unknown>[] = [];
  private _dialogConfigsAtThisLevel: (DialogConfig<unknown> | null)[] = [];
  private readonly desktopWidth = 'auto';

  constructor(
    private readonly _overlay: Overlay,
    private readonly _injector: Injector,
    @Optional() @SkipSelf() private readonly _parentDialog: DialogService,
    @Optional()
    @Inject(DIALOG_DEFAULT_OPTIONS)
    private readonly _defaultOptions?: DialogConfig
  ) {}

  /** All currently open dialog refs (bottom to top). */
  public get _openedDialogRefs(): DialogRef<unknown>[] {
    const parent = this._parentDialog;
    return parent ? parent._openedDialogRefs : this._dialogRefsAtThisLevel;
  }

  public set _openedDialogRefs(value: DialogRef<unknown>[]) {
    if (this._parentDialog) {
      this._parentDialog._openedDialogRefs = value;
    } else {
      this._dialogRefsAtThisLevel = value;
    }
  }

  /** The topmost open dialog ref, or null if none. */
  public get _topDialogRef(): DialogRef<unknown> | null {
    const refs = this._openedDialogRefs;
    return refs.length > 0 ? refs[refs.length - 1] : null;
  }

  /** All currently open dialog configs (bottom to top). */
  public get _openedDialogConfigs(): (DialogConfig<unknown> | null)[] {
    const parent = this._parentDialog;
    return parent ? parent._openedDialogConfigs : this._dialogConfigsAtThisLevel;
  }

  public set _openedDialogConfigs(value: (DialogConfig<unknown> | null)[]) {
    if (this._parentDialog) {
      this._parentDialog._openedDialogConfigs = value;
    } else {
      this._dialogConfigsAtThisLevel = value;
    }
  }

  /**
   * Opens a dialog containing the given component.
   *
   * @param component Type of the component to load into the dialog.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened dialog.
   */
  public open<T, D = unknown, R = unknown>(component: ComponentType<T>, config?: DialogConfig<D>): DialogRef<T, R>;

  /**
   * Opens a dialog containing the given template.
   *
   * @param template TemplateRef to instantiate as the dialog content.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened dialog.
   */

  public open<T, D = unknown, R = unknown>(template: TemplateRef<T>, config?: DialogConfig<D>): DialogRef<T, R>;

  public open<T, D = unknown, R = unknown>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: DialogConfig<D>
  ): DialogRef<T, R> {
    const _config = _applyConfigDefaults(this._defaultOptions || new DialogConfig(), config) as DialogConfig<D>;
    const overlayRef = this._createOverlay(_config);
    const container = this._attachContainer(overlayRef, _config);
    container.config = _config;
    const ref = new DialogRef<T, R>(container, overlayRef);

    if (componentOrTemplateRef instanceof TemplateRef) {
      container.attachTemplatePortal(
        new TemplatePortal<T>(componentOrTemplateRef, null!, {
          $implicit: _config.data,
          dialogRef: ref,
        } as unknown as T)
      );
    } else {
      const portal = new ComponentPortal(
        componentOrTemplateRef,
        undefined,
        this._createInjector(_config, ref as DialogRef<unknown, unknown>)
      );
      const contentRef = container.attachComponentPortal(portal);
      ref.instance = contentRef.instance;
    }

    // When the dialog is dismissed, remove it from the stacks.
    ref.afterDismissed().subscribe(() => {
      this._openedDialogRefs = this._openedDialogRefs.filter((r) => r !== ref);
      this._openedDialogConfigs = this._openedDialogConfigs.slice(0, this._openedDialogRefs.length);
    });

    ref.containerInstance.enter();

    this._openedDialogRefs = [...this._openedDialogRefs, ref as DialogRef<unknown, unknown>];
    this._openedDialogConfigs = [...this._openedDialogConfigs, config ?? null];

    return ref;
  }

  /**
   * Dismisses the currently-visible dialog.
   *
   * @param result Data to pass to the dialog instance.
   */
  public dismiss<R = unknown>(result?: R): void {
    if (this._topDialogRef) {
      this._topDialogRef.dismiss(result);
    }
  }

  public openConfirmationDialog(
    data: ConfirmationDialogViewModel,
    config?: DialogConfig<ConfirmationDialogViewModel>
  ): DialogRef<ConfirmationDialogComponent, boolean> {
    const defaultConfig: DialogConfig<ConfirmationDialogViewModel> = {
      data,
    };

    return this.open(ConfirmationDialogComponent, {
      ...defaultConfig,
      ...config,
      data,
    });
  }

  public ngOnDestroy(): void {
    for (const ref of this._dialogRefsAtThisLevel) {
      ref.dismiss();
    }
  }

  /**
   * Attaches the dialog container component to the overlay.
   */
  private _attachContainer(overlayRef: OverlayRef, config: DialogConfig): DialogContainerComponent {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const injector = Injector.create({
      parent: userInjector || this._injector,
      providers: [{ provide: DialogConfig, useValue: config }],
    });

    const containerPortal = new ComponentPortal(DialogContainerComponent, config.viewContainerRef, injector);
    const containerRef: ComponentRef<DialogContainerComponent> = overlayRef.attach(containerPortal);
    return containerRef.instance;
  }

  /**
   * Creates a new overlay and places it in the correct location.
   *
   * @param config The user-specified dialog config.
   */
  private _createOverlay(config: DialogConfig): OverlayRef {
    const overlayConfig = new OverlayConfig({
      direction: config.direction,
      hasBackdrop: config.hasBackdrop,
      backdropClass: 'dialog-backdrop',
      disposeOnNavigation: config.closeOnNavigation,
      maxWidth: this.getOverlayMaxWidth(config),
      width: this.getOverlayWidth(config),
      scrollStrategy: config.scrollStrategy || this._overlay.scrollStrategies.block(),
      positionStrategy: config.positionStrategy ?? this.getDialogPositionStrategy(),
      height: this.getOverlayHeight(config),
      maxHeight: this.getOverlayMaxHeight(config),
    });

    if (config.backdropClass) {
      overlayConfig.backdropClass = config.backdropClass;
    }

    return this._overlay.create(overlayConfig);
  }

  /**
   * Creates an injector to be used inside of a dialog component.
   *
   * @param config Config that was used to create the dialog.
   * @param dialogRef Reference to the dialog.
   */
  private _createInjector<T>(config: DialogConfig, dialogRef: DialogRef<T>): Injector {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const providers: StaticProvider[] = [
      { provide: DialogRef, useValue: dialogRef },
      { provide: DIALOG_DATA, useValue: config.data },
    ];

    if (config.direction && (!userInjector || !userInjector.get<Directionality | null>(Directionality, null, { optional: true }))) {
      providers.push({
        provide: Directionality,
        useValue: { value: config.direction, change: observableOf() },
      });
    }

    return Injector.create({ parent: userInjector || this._injector, providers });
  }

  private getOverlayWidth(config?: DialogConfig | null): string {
    return config?.width || this.desktopWidth;
  }

  private getOverlayHeight(config?: DialogConfig | null): string {
    return config?.height || 'auto';
  }

  private getOverlayMaxWidth(config?: DialogConfig | null): string {
    return config?.maxWidth || '100%';
  }

  private getOverlayMaxHeight(config?: DialogConfig | null): string {
    return config?.maxHeight || '100%';
  }

  private getDialogPositionStrategy(): PositionStrategy {
    return this._overlay.position().global().centerHorizontally().centerVertically();
  }
}
